import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import DashboardShell from './components/DashboardShell';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function buildUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.detail || data?.error || `Request failed (${response.status})`);
  return data;
}

function dateValue(value) {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}

function localDateTimeValue(value) {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatDate(value, includeTime = false) {
  if (!value) return '—';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

function daysUntil(value) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const meeting = new Date(value);
  meeting.setHours(0, 0, 0, 0);
  return Math.round((meeting - today) / 86400000);
}

function copyText(text) {
  if (navigator.clipboard) return navigator.clipboard.writeText(text);
  window.prompt('Copy this text:', text);
  return Promise.resolve();
}

const CATEGORY_LABELS = {
  amended_plan: 'Amended Plan',
  document_request: 'Request Documents',
  amendments: 'Prepare Amendments',
  other: 'Other Work',
};

const AMENDMENT_OPTIONS = [
  ['petition', 'Petition'],
  ['schedule_ab', 'Schedules A/B'],
  ['schedule_c', 'Schedule C'],
  ['schedule_d', 'Schedule D'],
  ['schedule_ef', 'Schedules E/F'],
  ['schedule_ij', 'Schedules I/J'],
  ['sofa', 'SOFA'],
  ['means_test', 'Means test'],
  ['chapter_13_plan', 'Chapter 13 plan'],
  ['other', 'Other'],
];

const STATUS_STYLES = {
  open: 'bg-red-50 text-red-700 ring-red-200',
  waiting_client: 'bg-amber-50 text-amber-800 ring-amber-200',
  ready: 'bg-blue-50 text-blue-700 ring-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  cancelled: 'bg-slate-100 text-slate-500 ring-slate-200',
};

function SummaryCard({ label, value, icon: Icon, tone = 'indigo' }) {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span className={`rounded-lg p-2 ${tones[tone]}`}><Icon className="h-6 w-6" /></span>
        <div><p className="text-2xl font-bold text-slate-900">{value || 0}</p><p className="text-sm text-slate-600">{label}</p></div>
      </div>
    </div>
  );
}

function EmployeeSelect({ employees, value, onChange, className = '' }) {
  return (
    <select value={value || ''} onChange={(event) => onChange(event.target.value)} className={`rounded-md border border-slate-300 px-3 py-2 text-sm ${className}`}>
      <option value="">Unassigned</option>
      {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.full_name}</option>)}
    </select>
  );
}

function WorkItemCard({ item, review, employees, onUpdate, onDelete, busy }) {
  const [copied, setCopied] = useState(false);
  const isOverdue = item.due_date && dateValue(item.due_date) < dateValue(new Date()) && !['completed', 'cancelled'].includes(item.status);
  const composeEmail = () => {
    const subject = encodeURIComponent(`Documents needed before your ${formatDate(review.meeting_at)} 341 meeting`);
    const body = encodeURIComponent(item.request_message || item.description || '');
    const recipients = (review.case.debtor_emails || []).join(',');
    window.location.href = `mailto:${recipients}?subject=${subject}&body=${body}`;
  };
  return (
    <article className={`rounded-lg border p-4 ${isOverdue ? 'border-red-300 bg-red-50/40' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-semibold text-slate-900">{item.title}</h4>
            <span className={`rounded-full px-2 py-1 text-xs font-semibold ring-1 ring-inset ${STATUS_STYLES[item.status]}`}>{item.status_display}</span>
            {isOverdue && <span className="text-xs font-bold text-red-700">OVERDUE</span>}
          </div>
          <p className="mt-1 text-sm text-slate-600">{CATEGORY_LABELS[item.category]} · Due {formatDate(item.due_date)} · Priority {item.priority}</p>
          {item.description && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{item.description}</p>}
          {item.checklist_items?.length > 0 && <p className="mt-2 text-sm text-slate-700"><strong>Documents:</strong> {item.checklist_items.map((entry) => entry.name).join(', ')}</p>}
          {item.amendment_sections?.length > 0 && <p className="mt-2 text-sm text-slate-700"><strong>Amend:</strong> {item.amendment_sections.map((key) => AMENDMENT_OPTIONS.find(([value]) => value === key)?.[1] || key).join(', ')}</p>}
        </div>
        <button type="button" onClick={() => onDelete(item)} disabled={busy} className="text-slate-400 hover:text-red-600" title="Remove work item"><TrashIcon className="h-5 w-5" /></button>
      </div>

      {item.request_message && (
        <div className="mt-3 rounded-md bg-slate-50 p-3">
          <p className="max-h-28 overflow-auto whitespace-pre-wrap text-sm text-slate-700">{item.request_message}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={async () => { await copyText(item.request_message); setCopied(true); window.setTimeout(() => setCopied(false), 1500); }} className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"><ClipboardDocumentIcon className="h-4 w-4" />{copied ? 'Copied' : 'Copy request'}</button>
            <button type="button" onClick={composeEmail} className="inline-flex items-center gap-1 rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700"><EnvelopeIcon className="h-4 w-4" />Compose email</button>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <EmployeeSelect employees={employees} value={item.assigned_employee?.id} onChange={(value) => onUpdate(item, { assigned_employee: value })} />
        {item.status !== 'open' && item.status !== 'cancelled' && <button type="button" disabled={busy} onClick={() => onUpdate(item, { status: 'open' })} className="rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700">Needs action</button>}
        {item.status !== 'waiting_client' && item.status !== 'cancelled' && <button type="button" disabled={busy} onClick={() => onUpdate(item, { status: 'waiting_client' })} className="rounded-md border border-amber-300 px-3 py-2 text-xs font-semibold text-amber-800">Waiting on client</button>}
        {item.status !== 'ready' && item.status !== 'cancelled' && <button type="button" disabled={busy} onClick={() => onUpdate(item, { status: 'ready' })} className="rounded-md border border-blue-300 px-3 py-2 text-xs font-semibold text-blue-700">Ready</button>}
        {item.status !== 'completed' && item.status !== 'cancelled' && <button type="button" disabled={busy} onClick={() => onUpdate(item, { status: 'completed' })} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white"><CheckCircleIcon className="h-4 w-4" />Complete</button>}
        {item.status === 'completed' && <button type="button" disabled={busy} onClick={() => onUpdate(item, { status: 'open' })} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700"><ArrowPathIcon className="h-4 w-4" />Reopen</button>}
      </div>
    </article>
  );
}

function AddWorkItem({ review, employees, onCreated, onCancel }) {
  const [category, setCategory] = useState(review.case.chapter === '13' ? 'amended_plan' : 'document_request');
  const [form, setForm] = useState({ title: '', description: '', due_date: '', assigned_employee: review.assigned_employee?.id || '', priority: 3 });
  const [checklistIds, setChecklistIds] = useState([]);
  const [availableChecklist, setAvailableChecklist] = useState(review.checklist_items || []);
  const [newDocument, setNewDocument] = useState({
    name: '',
    description: '',
    post_filing_debtor_requirement: true,
  });
  const [creatingDocument, setCreatingDocument] = useState(false);
  const [documentMessage, setDocumentMessage] = useState('');
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const toggle = (list, setList, value) => setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  const createDocumentItem = async () => {
    const name = newDocument.name.trim();
    if (!name) {
      setDocumentMessage('Enter a document name first.');
      return;
    }
    const existing = availableChecklist.find((item) => item.name.trim().toLowerCase() === name.toLowerCase());
    if (existing) {
      if (!checklistIds.includes(existing.id)) setChecklistIds([...checklistIds, existing.id]);
      setDocumentMessage('That document item already exists, so it was selected.');
      return;
    }
    setCreatingDocument(true); setDocumentMessage('');
    try {
      const created = await apiFetch(`/case/${review.case.case_id}/checklist/`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'add',
          name,
          description: newDocument.description.trim(),
          post_filing_debtor_requirement: newDocument.post_filing_debtor_requirement,
        }),
      });
      const checklistItem = {
        ...created,
        received: false,
        upload_count: 0,
      };
      setAvailableChecklist((items) => [...items, checklistItem].sort((a, b) => a.name.localeCompare(b.name)));
      setChecklistIds((ids) => [...ids, checklistItem.id]);
      setNewDocument({
        name: '',
        description: '',
        post_filing_debtor_requirement: true,
      });
      setDocumentMessage(`Created and selected “${checklistItem.name}.”`);
    } catch (err) {
      setDocumentMessage(err.message);
    } finally {
      setCreatingDocument(false);
    }
  };
  const togglePostFilingRequirement = async (item) => {
    setDocumentMessage('');
    try {
      const updated = await apiFetch(`/case/${review.case.case_id}/checklist/`, {
        method: 'POST',
        body: JSON.stringify({
          action: 'update_post_filing_requirement',
          item_id: item.id,
          enabled: !item.post_filing_debtor_requirement,
        }),
      });
      setAvailableChecklist((items) => items.map((entry) => (
        entry.id === item.id
          ? { ...entry, post_filing_debtor_requirement: updated.post_filing_debtor_requirement }
          : entry
      )));
      setDocumentMessage(
        updated.post_filing_debtor_requirement
          ? `“${item.name}” will receive automated debtor reminders.`
          : `Automated debtor reminders were removed from “${item.name}.”`,
      );
    } catch (err) {
      setDocumentMessage(err.message);
    }
  };
  const submit = async (event) => {
    event.preventDefault(); setSaving(true); setError('');
    try {
      const result = await apiFetch(`/firm/341-workboard/reviews/${review.id}/work-items/`, {
        method: 'POST',
        body: JSON.stringify({ ...form, category, checklist_item_ids: checklistIds, amendment_sections: sections }),
      });
      onCreated(result);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4">
      <div className="flex flex-wrap gap-2">
        {Object.entries(CATEGORY_LABELS).filter(([value]) => value !== 'amended_plan' || review.case.chapter === '13').map(([value, label]) => (
          <button key={value} type="button" onClick={() => setCategory(value)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${category === value ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700 ring-1 ring-slate-300'}`}>{label}</button>
        ))}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-700">Title<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="A useful action title (optional)" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" /></label>
        <label className="text-sm font-medium text-slate-700">Due date<input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" /><span className="mt-1 block text-xs font-normal text-slate-500">Leave blank for the recommended deadline.</span></label>
        <label className="text-sm font-medium text-slate-700 md:col-span-2">Details<textarea rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" /></label>
        <label className="text-sm font-medium text-slate-700">Assigned employee<EmployeeSelect employees={employees} value={form.assigned_employee} onChange={(value) => setForm({ ...form, assigned_employee: value })} className="mt-1 w-full" /></label>
        <label className="text-sm font-medium text-slate-700">Priority<select value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"><option value="1">1 — Urgent</option><option value="2">2 — High</option><option value="3">3 — Normal</option><option value="4">4 — Low</option><option value="5">5 — Someday</option></select></label>
      </div>
      {category === 'document_request' && (
        <fieldset className="mt-4">
          <legend className="text-sm font-semibold text-slate-800">Documents to request</legend>
          <div className="mt-2 rounded-lg border border-dashed border-indigo-300 bg-white p-3">
            <p className="text-sm font-semibold text-slate-800">Create a new case document item</p>
            <p className="mt-1 text-xs text-slate-500">The new item will also appear on the case’s regular document checklist and will be selected for this request.</p>
            <div className="mt-3 grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_auto]">
              <input
                value={newDocument.name}
                onChange={(event) => setNewDocument({ ...newDocument, name: event.target.value })}
                placeholder="Document name, e.g. June bank statement"
                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                value={newDocument.description}
                onChange={(event) => setNewDocument({ ...newDocument, description: event.target.value })}
                placeholder="Instructions or description (optional)"
                className="rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={createDocumentItem}
                disabled={creatingDocument}
                className="inline-flex items-center justify-center gap-1 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
              >
                <PlusIcon className="h-4 w-4" />
                {creatingDocument ? 'Creating…' : 'Create and select'}
              </button>
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={newDocument.post_filing_debtor_requirement}
                onChange={(event) => setNewDocument({
                  ...newDocument,
                  post_filing_debtor_requirement: event.target.checked,
                })}
              />
              Post-filing requirement from debtor — send automatic reminders 21, 14, 7, 3, and 1 days before the 341 until received
            </label>
            {documentMessage && <p className="mt-2 text-xs font-medium text-indigo-700">{documentMessage}</p>}
          </div>
          {availableChecklist.length ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {availableChecklist.map((item) => (
                <div key={item.id} className="rounded-md bg-white p-2 text-sm ring-1 ring-slate-200">
                  <label className="flex items-start gap-2">
                    <input type="checkbox" checked={checklistIds.includes(item.id)} onChange={() => toggle(checklistIds, setChecklistIds, item.id)} className="mt-1" />
                    <span>{item.name}{item.received && <span className="ml-1 text-xs font-semibold text-emerald-700">Received</span>}{item.description && <span className="mt-0.5 block text-xs text-slate-500">{item.description}</span>}</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => togglePostFilingRequirement(item)}
                    className={`mt-2 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                      item.post_filing_debtor_requirement
                        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                        : 'bg-slate-50 text-slate-600 ring-slate-200'
                    }`}
                  >
                    {item.post_filing_debtor_requirement
                      ? 'Debtor reminders on'
                      : 'Mark as debtor requirement'}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">No document items exist yet. Create the first one above.</p>
          )}
        </fieldset>
      )}
      {category === 'amendments' && (
        <fieldset className="mt-4"><legend className="text-sm font-semibold text-slate-800">Sections to amend</legend><div className="mt-2 flex flex-wrap gap-2">{AMENDMENT_OPTIONS.map(([value, label]) => <label key={value} className="flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm ring-1 ring-slate-200"><input type="checkbox" checked={sections.includes(value)} onChange={() => toggle(sections, setSections, value)} />{label}</label>)}</div></fieldset>
      )}
      {error && <p className="mt-3 text-sm font-medium text-red-700">{error}</p>}
      <div className="mt-4 flex gap-2"><button type="submit" disabled={saving} className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Adding…' : 'Add to workboard and Today’s ToDos'}</button><button type="button" onClick={onCancel} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700">Cancel</button></div>
    </form>
  );
}

function ReviewCard({ review, employees, initiallyOpen, reload, onReviewUpdated }) {
  const [open, setOpen] = useState(initiallyOpen);
  const [adding, setAdding] = useState(false);
  const [notes, setNotes] = useState({ preparation_notes: review.preparation_notes || '', hearing_notes: review.hearing_notes || '' });
  const [outcome, setOutcome] = useState(review.outcome);
  const [continuedTo, setContinuedTo] = useState(localDateTimeValue(review.continued_to));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const dayCount = daysUntil(review.meeting_at);
  const activeCount = review.work_items.filter((item) => ['open', 'ready'].includes(item.status)).length;
  const patchReview = async (payload, success = 'Saved') => {
    setBusy(true); setMessage('');
    try {
      const updatedReview = await apiFetch(`/firm/341-workboard/reviews/${review.id}/`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      onReviewUpdated(updatedReview);
      setMessage(success);
    }
    catch (err) { setMessage(err.message); } finally { setBusy(false); }
  };
  const updateItem = async (item, payload) => {
    setBusy(true); setMessage('');
    try { await apiFetch(`/firm/341-workboard/work-items/${item.id}/`, { method: 'PATCH', body: JSON.stringify(payload) }); await reload(); }
    catch (err) { setMessage(err.message); } finally { setBusy(false); }
  };
  const deleteItem = async (item) => {
    if (!window.confirm(`Remove “${item.title}” from the workboard and Today’s ToDos?`)) return;
    setBusy(true);
    try { await apiFetch(`/firm/341-workboard/work-items/${item.id}/`, { method: 'DELETE' }); await reload(); }
    catch (err) { setMessage(err.message); } finally { setBusy(false); }
  };
  return (
    <section id={`review-${review.id}`} className="review-print-break rounded-xl border border-slate-200 bg-white shadow-sm">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start justify-between gap-4 p-5 text-left">
        <div>
          <div className="flex flex-wrap items-center gap-2"><h2 className="text-lg font-bold text-slate-900">{review.case.name}</h2><span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">Chapter {review.case.chapter}</span>{activeCount > 0 && <span className="rounded-full bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">{activeCount} need action</span>}</div>
          <p className="mt-1 text-sm text-slate-600">{review.case.case_number || review.case.case_id} · {formatDate(review.meeting_at, true)} · {dayCount >= 0 ? `${dayCount} days away` : `${Math.abs(dayCount)} days ago`}</p>
          <p className="mt-1 text-sm text-slate-500">Trustee: {review.case.trustee || 'Not listed'}{review.assigned_employee ? ` · Lead: ${review.assigned_employee.full_name}` : ''}</p>
        </div>
        <ChevronDownIcon className={`mt-1 h-5 w-5 shrink-0 text-slate-500 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-slate-200 p-5">
          {review.case.chapter === '13' && <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"><strong>Latest safe amended-plan filing date:</strong> {formatDate(review.latest_safe_amended_plan_date)} (21 days before the 341, moved earlier if it lands on a weekend).</div>}
          <div className="grid gap-4 lg:grid-cols-2">
            <label className="text-sm font-semibold text-slate-800">Preparation notes<textarea rows="6" value={notes.preparation_notes} onChange={(e) => setNotes({ ...notes, preparation_notes: e.target.value })} onBlur={() => patchReview({ preparation_notes: notes.preparation_notes }, 'Preparation notes saved')} placeholder="What must be handled before this 341?" className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 font-normal" /></label>
            <label className="text-sm font-semibold text-slate-800">Hearing notes<textarea rows="6" value={notes.hearing_notes} onChange={(e) => setNotes({ ...notes, hearing_notes: e.target.value })} onBlur={() => patchReview({ hearing_notes: notes.hearing_notes }, 'Hearing notes saved')} placeholder="Questions, trustee requests, continuance details, and hearing results" className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 font-normal" /></label>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-3 rounded-lg bg-slate-50 p-3">
            <label className="text-sm font-semibold text-slate-700">Workboard lead<EmployeeSelect employees={employees} value={review.assigned_employee?.id} onChange={(value) => patchReview({ assigned_employee: value }, 'Lead updated')} className="mt-1 block" /></label>
            <label className="text-sm font-semibold text-slate-700">Outcome<select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="mt-1 block rounded-md border border-slate-300 px-3 py-2"><option value="pending">Pending</option><option value="concluded">Concluded</option><option value="continued">Continued</option><option value="no_show">Debtor no-show</option></select></label>
            {outcome === 'continued' && <label className="text-sm font-semibold text-slate-700">Continued to<input type="datetime-local" value={continuedTo} onChange={(e) => setContinuedTo(e.target.value)} className="mt-1 block rounded-md border border-slate-300 px-3 py-2" /></label>}
            <button type="button" disabled={busy} onClick={() => patchReview({ outcome, continued_to: outcome === 'continued' && continuedTo ? new Date(continuedTo).toISOString() : null }, outcome === 'continued' ? '341 continued and open work carried forward' : 'Outcome saved')} className="rounded-md bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Save outcome</button>
            <a href={`/case/${review.case.case_id}`} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700">Open case</a>
          </div>
          {message && <p className={`mt-3 text-sm font-medium ${message.toLowerCase().includes('saved') || message.toLowerCase().includes('updated') || message.toLowerCase().includes('carried') ? 'text-emerald-700' : 'text-red-700'}`}>{message}</p>}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3"><h3 className="font-bold text-slate-900">Work items</h3><button type="button" onClick={() => setAdding(!adding)} className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white"><PlusIcon className="h-4 w-4" />Add work</button></div>
          {adding && <AddWorkItem review={review} employees={employees} onCancel={() => setAdding(false)} onCreated={async () => { setAdding(false); await reload(); }} />}
          <div className="mt-4 space-y-3">{review.work_items.length ? review.work_items.map((item) => <WorkItemCard key={item.id} item={item} review={review} employees={employees} onUpdate={updateItem} onDelete={deleteItem} busy={busy} />) : <p className="rounded-lg border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">No follow-up work has been recorded for this 341 yet.</p>}</div>
          {review.previous_reviews?.length > 0 && <details className="mt-5 rounded-lg border border-slate-200 p-3"><summary className="cursor-pointer text-sm font-semibold text-slate-700">Prior 341 dates and notes ({review.previous_reviews.length})</summary><div className="mt-3 space-y-3">{review.previous_reviews.map((previous) => <div key={previous.id} className="rounded-md bg-slate-50 p-3 text-sm"><strong>{formatDate(previous.meeting_at, true)} · {previous.outcome_display}</strong>{previous.preparation_notes && <p className="mt-2 whitespace-pre-wrap"><strong>Preparation:</strong> {previous.preparation_notes}</p>}{previous.hearing_notes && <p className="mt-2 whitespace-pre-wrap"><strong>Hearing:</strong> {previous.hearing_notes}</p>}</div>)}</div></details>}
        </div>
      )}
    </section>
  );
}

export default function Meeting341WorkboardPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const focusedReview = params.get('review');
  const today = new Date();
  const future = new Date(); future.setDate(future.getDate() + 180);
  const [filters, setFilters] = useState({ q: '', chapter: '', trustee: '', employee: '', work_state: '', meeting_from: dateValue(new Date(today.getTime() - 30 * 86400000)), meeting_to: dateValue(future) });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [data, setData] = useState({ reviews: [], employees: [], trustees: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = async ({ showLoading = false, scrollToReviewId = null } = {}) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams(Object.entries(appliedFilters).filter(([, value]) => value)).toString();
      const result = await apiFetch(`/firm/341-workboard/?${query}`);
      setData(result);
      if (scrollToReviewId) {
        window.setTimeout(() => {
          document.getElementById(`review-${scrollToReviewId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  };
  const mergeUpdatedReview = (updatedReview) => {
    setData((current) => ({
      ...current,
      reviews: current.reviews.map((currentReview) => (
        currentReview.id === updatedReview.id
        || currentReview.case.case_id === updatedReview.case.case_id
          ? updatedReview
          : currentReview
      )),
    }));
  };
  useEffect(() => {
    load({ showLoading: true, scrollToReviewId: focusedReview });
  }, [appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <DashboardShell>
      <style>{`@media print { aside, nav, header, button, .no-print { display:none !important; } .lg\\:pl-72 { padding-left:0 !important; } main { padding:0 !important; } .review-print-break { break-inside:avoid; box-shadow:none !important; } textarea { border:0 !important; resize:none; } }`}</style>
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><h1 className="text-3xl font-bold tracking-tight text-slate-900">341 Workboard</h1><p className="mt-2 max-w-3xl text-slate-600">Centralize preparation notes, hearing notes, document requests, amendments, and Chapter 13 amended-plan deadlines. Actionable work also appears in Today’s ToDos.</p></div>
          <button type="button" onClick={() => window.print()} className="no-print inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"><PrinterIcon className="h-5 w-5" />Print workboard</button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4"><SummaryCard label="Meetings shown" value={data.summary?.upcoming_meetings} icon={CalendarDaysIcon} /><SummaryCard label="Need action" value={data.summary?.actionable} icon={DocumentTextIcon} tone="blue" /><SummaryCard label="Waiting on client" value={data.summary?.waiting_client} icon={ClockIcon} tone="amber" /><SummaryCard label="Overdue" value={data.summary?.overdue} icon={ExclamationTriangleIcon} tone="red" /></div>
        <form onSubmit={(e) => { e.preventDefault(); setAppliedFilters({ ...filters }); }} className="no-print mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search name or case number" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
            <select value={filters.chapter} onChange={(e) => setFilters({ ...filters, chapter: e.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">All chapters</option><option value="7">Chapter 7</option><option value="13">Chapter 13</option></select>
            <select value={filters.employee} onChange={(e) => setFilters({ ...filters, employee: e.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">All employees</option><option value="unassigned">Unassigned</option>{data.employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.full_name}</option>)}</select>
            <select value={filters.trustee} onChange={(e) => setFilters({ ...filters, trustee: e.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">All trustees</option>{data.trustees.map((trustee) => <option key={trustee} value={trustee}>{trustee}</option>)}</select>
            <select value={filters.work_state} onChange={(e) => setFilters({ ...filters, work_state: e.target.value })} className="rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">All work states</option><option value="actionable">Needs action</option><option value="waiting">Waiting on client</option><option value="overdue">Overdue</option><option value="complete">Completed</option></select>
            <label className="flex items-center gap-2 text-sm text-slate-600">From<input type="date" value={filters.meeting_from} onChange={(e) => setFilters({ ...filters, meeting_from: e.target.value })} className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-2" /></label>
            <label className="flex items-center gap-2 text-sm text-slate-600">Through<input type="date" value={filters.meeting_to} onChange={(e) => setFilters({ ...filters, meeting_to: e.target.value })} className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-2" /></label>
            <button type="submit" className="rounded-md bg-slate-800 px-4 py-2 text-sm font-semibold text-white">Apply filters</button>
          </div>
        </form>
        {error && <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">{error}</div>}
        {loading ? <div className="mt-8 text-center text-slate-500">Loading 341 workboard…</div> : <div className="mt-6 space-y-4">{data.reviews.length ? data.reviews.map((review) => <ReviewCard key={review.case.case_id} review={review} employees={data.employees} initiallyOpen={!focusedReview || focusedReview === review.id} reload={load} onReviewUpdated={mergeUpdatedReview} />) : <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center"><CalendarDaysIcon className="mx-auto h-10 w-10 text-slate-400" /><h2 className="mt-3 font-semibold text-slate-900">No 341 meetings match these filters</h2><p className="mt-1 text-sm text-slate-500">The workboard uses the 341 meeting date saved on filed and confirmed cases.</p></div>}</div>}
      </div>
    </DashboardShell>
  );
}
